// Dialog riusabile: invia un documento PDF via email dalla casella personale dell'utente.
// Una mail separata per ogni destinatario (i clienti non vedono gli altri indirizzi).
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Send, Loader2, X, AlertTriangle, Paperclip } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function SendDocEmailDialog({ open, onClose, title, attachmentLabel, endpoint, subjectDefault = "", bodyDefault = "", langSelector = false, presetEmails = [], presetCustomerId = null, extraPayload = null, settingsPath = null }) {
  const [recipients, setRecipients] = useState([]);
  const [query, setQuery] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [lang, setLang] = useState("it");
  const [sending, setSending] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    if (!open) return;
    setRecipients((presetEmails || []).filter((e) => e && EMAIL_RE.test(e)));
    setQuery("");
    setSubject(subjectDefault);
    setBody(bodyDefault);
    setLang("it");
    axios.get(`${API}/email-settings`).then((r) => setCfg(r.data || {})).catch(() => setCfg({}));
    axios.get(`${API}/customers`).then((r) => {
      const list = r.data || [];
      setCustomers(list);
      if (presetCustomerId && !(presetEmails || []).length) {
        const c = list.find((x) => x.id === presetCustomerId);
        if (c?.email && EMAIL_RE.test(c.email)) setRecipients((prev) => (prev.includes(c.email) ? prev : [...prev, c.email]));
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const effSettingsPath = settingsPath || (() => {
    const p = window.location.pathname;
    if (p.startsWith("/admin")) return "/admin?tab=settings";
    if (p.startsWith("/warehouse")) return "/warehouse/settings";
    return "/agent/settings";
  })();

  const configured = cfg && cfg.smtp_host && cfg.imap_host && cfg.smtp_user && cfg.has_password;

  const addRecipient = (email) => {
    const e = (email || "").trim();
    if (!e || !EMAIL_RE.test(e)) { toast.error("Indirizzo email non valido"); return; }
    setRecipients((r) => (r.includes(e) ? r : [...r, e]));
    setQuery("");
  };

  const suggestions = query.length >= 2
    ? customers.filter((c) => c.email && !recipients.includes(c.email) &&
        ((c.business_name || "").toLowerCase().includes(query.toLowerCase()) || c.email.toLowerCase().includes(query.toLowerCase()))).slice(0, 6)
    : [];

  const send = async () => {
    if (recipients.length === 0) { toast.error("Aggiungi almeno un destinatario"); return; }
    setSending(true);
    try {
      const payload = { recipients, subject, body, ...(extraPayload || {}) };
      if (langSelector) payload.lang = lang;
      const r = await axios.post(endpoint, payload);
      const nSent = (r.data?.sent || []).length;
      const errs = r.data?.errors || [];
      if (nSent > 0) toast.success(`Email inviata a ${nSent} destinatari${nSent === 1 ? "o" : ""}`);
      if (errs.length > 0) toast.error(`${errs.length} invii falliti: ${errs[0].email} — ${errs[0].error}`);
      if (errs.length === 0) onClose();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(d === "not_configured" ? "Casella email non configurata" : (d || "Errore nell'invio"));
    } finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="send-doc-mail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#8A1C2A]" />{title || "Invia via email"}</DialogTitle>
        </DialogHeader>

        {cfg && !configured && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm flex gap-2" data-testid="send-mail-not-configured">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-amber-800">
              La tua casella email non è configurata: per inviare devi prima impostarla in{" "}
              <Link to={effSettingsPath} className="underline font-semibold" onClick={onClose}>Impostazioni</Link>.
            </p>
          </div>
        )}

        {attachmentLabel && (
          <div className="flex items-center gap-2 text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-md px-3 py-2" data-testid="send-mail-attachment">
            <Paperclip className="w-3.5 h-3.5 text-[#8A1C2A]" /> Allegato: <b>{attachmentLabel}</b>
          </div>
        )}

        {langSelector && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-stone-500">Lingua PDF:</span>
            <div className="flex gap-1">
              <button onClick={() => setLang("it")} className={`px-3 py-1 rounded-md text-xs font-semibold border ${lang === "it" ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`} data-testid="send-mail-lang-it">🇮🇹 Italiano</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1 rounded-md text-xs font-semibold border ${lang === "en" ? "bg-[#8A1C2A] text-white border-[#8A1C2A]" : "border-stone-300 text-stone-600 hover:bg-stone-50"}`} data-testid="send-mail-lang-en">🇬🇧 English</button>
            </div>
          </div>
        )}

        <div>
          <p className="text-xs font-semibold text-stone-600 mb-1">Destinatari (una mail separata per ciascuno)</p>
          {recipients.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {recipients.map((e) => (
                <Badge key={e} className="bg-stone-100 text-stone-800 border border-stone-300 gap-1 font-normal" data-testid={`send-mail-chip-${e}`}>
                  {e}
                  <button onClick={() => setRecipients((r) => r.filter((x) => x !== e))} className="hover:text-red-600" data-testid={`send-mail-chip-remove-${e}`}><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          )}
          <div className="relative">
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addRecipient(query); } }}
              placeholder="Cerca cliente o digita un'email e premi Invio…"
              data-testid="send-mail-recipient-input"
            />
            {suggestions.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-stone-200 rounded-md shadow-lg max-h-48 overflow-y-auto" data-testid="send-mail-suggestions">
                {suggestions.map((c) => (
                  <button key={c.id} type="button" onClick={() => addRecipient(c.email)} className="w-full text-left px-3 py-2 hover:bg-stone-50 border-b border-stone-100 last:border-b-0" data-testid={`send-mail-suggestion-${c.codice_cliente || c.id}`}>
                    <p className="text-sm font-medium">{c.business_name}</p>
                    <p className="text-xs text-stone-500">{c.email}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Oggetto" data-testid="send-mail-subject" />
          <Textarea value={body} onChange={(e) => setBody(e.target.value)} rows={6} placeholder="Messaggio…" data-testid="send-mail-body" />
        </div>

        <Button onClick={send} disabled={sending || !configured || recipients.length === 0} className="bg-[#8A1C2A] hover:bg-[#6e1622] w-full" data-testid="send-mail-submit">
          {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
          Invia {recipients.length > 1 ? `${recipients.length} email` : "email"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
